"use client";

import { useMemo, useState } from "react";
import type { DealRow, DealStatus } from "../lib/data";
import { money } from "../lib/format";
import Icon from "./Icon";
import { Skeleton } from "./Skeleton";
import { PanelError } from "./PanelState";

const statusStyle: Record<DealStatus, string> = {
  Active: "var(--accent)",
  "Pending Follow-Up": "var(--warn)",
  "Closed (Sold)": "var(--ok)",
  "Closed (Lost)": "var(--danger)",
};

type SortKey = "customer" | "bells" | "status" | "gross";
type Dir = "asc" | "desc";

interface Props {
  rows?: DealRow[];
  title?: string;
  action?: React.ReactNode;
  onRowClick?: (deal: DealRow) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyHint?: string;
}

export default function DealsTable({
  rows = [],
  title = "Active Deals",
  action,
  onRowClick,
  loading = false,
  error = null,
  onRetry,
  emptyHint = "Try a different filter or search.",
}: Props) {
  const [sort, setSort] = useState<SortKey | null>(null);
  const [dir, setDir] = useState<Dir>("asc");

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, dir]);

  const toggle = (k: SortKey) => {
    if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setDir("asc"); }
  };

  const Th = ({ k, label, align = "left" }: { k?: SortKey; label: string; align?: "left" | "right" }) => (
    <th
      scope="col"
      aria-sort={k ? (sort === k ? (dir === "asc" ? "ascending" : "descending") : "none") : undefined}
      className={`font-medium px-3 py-1 ${align === "right" ? "text-right" : "text-left"}`}
    >
      {k ? (
        <button
          onClick={() => toggle(k)}
          aria-label={`Sort by ${label}`}
          className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""} hover:text-text transition-colors ${sort === k ? "text-accent" : ""}`}
        >
          {label}
          <Icon name={sort === k && dir === "desc" ? "arrow-down" : "arrow-up"} size={11} className={sort === k ? "opacity-100" : "opacity-30"} />
        </button>
      ) : label}
    </th>
  );

  return (
    <div className="neu p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg text-text">{title}</h2>
        {/* The count is unknown while loading — "0 records" would read as an
            empty result rather than a pending one. */}
        {action ??
          (loading ? (
            <Skeleton className="h-3.5 w-20 rounded" />
          ) : (
            <span className="text-[12.5px] text-text-faint">{rows.length} records</span>
          ))}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-13 rounded-2xl" />
          ))}
        </div>
      ) : error ? (
        <PanelError message={error} onRetry={onRetry} />
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
          <span className="neu-sm grid place-items-center w-14 h-14 rounded-2xl text-text-faint">
            <Icon name="search" size={22} />
          </span>
          <p className="text-[14px] font-medium text-text">No deals match</p>
          <p className="text-[12.5px] text-text-faint">{emptyHint}</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1 px-1">
          <table aria-label={title} className="w-full min-w-160 border-separate border-spacing-y-2">
            <thead>
              <tr className="text-[11.5px] uppercase tracking-wide text-text-faint">
                <Th k="customer" label="Deal" />
                <Th label="Vehicle" />
                <Th label="Salesperson" />
                <Th k="bells" label="Bells" />
                <Th k="status" label="Status" />
                <Th k="gross" label="Gross" align="right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => onRowClick?.(d)}
                  className={`text-[13.5px] text-text ${onRowClick ? "cursor-pointer" : ""} group`}
                >
                  <td className="rounded-l-2xl px-3 py-3 font-semibold group-hover:text-accent transition-colors">
                    <span className="block">{d.customer}</span>
                    <span className="block text-[11.5px] text-text-faint font-normal">#{d.id}</span>
                  </td>
                  <td className="px-3 py-3 text-text-muted">{d.vehicle}</td>
                  <td className="px-3 py-3 text-text-muted">{d.salesperson}</td>
                  <td className="px-3 py-3">
                    <span className="neu-inset inline-grid place-items-center w-7 h-7 rounded-lg text-[12px] font-semibold">
                      {d.bells}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-[12px] font-medium"
                      style={{ color: statusStyle[d.status] }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusStyle[d.status] }} />
                      {d.status}
                    </span>
                  </td>
                  <td className="neu-sm rounded-r-2xl px-3 py-3 text-right font-display">
                    {d.gross === 0 ? "—" : money(d.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
